import Loader from '@cpg-its/ads.common.loading-circle';
import AuthContext from '@cpg-its/ads.auth.auth-context';
import React, { useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, Route, Switch, useHistory, useLocation, useRouteMatch } from 'react-router';
import { getDcPlanDetail, getRsvpPlanDetail } from '../../../../../../global/ducks/benadmin-api';
import { useBasePath } from '../../../../../../global/hooks/useBasePath';
import { useQuery } from '../../../../../../global/hooks/useQuery';
import { DemographicAppKeys } from '../../../../../../global/models/demographics-model';
import { Institution } from '../../../../../../global/models/institution-model';
import { Person } from '../../../../../../global/models/person-model';
import { TcsmEpiApiState, TcsmStates } from '../../../../../../root/tcsm-model';
import { useDemographicsPerson } from '../../../../../account-info/hooks/useDemographicsPerson';
import { ArcRoutes } from '../../../../constants/routes';
import { InstitutionState } from '../../institution-model';
import { PensionPlanDetail } from '../../plan-history/plan-history-models';
import { usePlanSummary } from '../../plan-summary/usePlanSummary';
import PlanAdoptionStepper from '../display-components/plan-adoption-stepper';
import { getAdoptionStepFromLocation, GetQccoAmendmentWizard } from '../factory/plan-adoption-wizard-factory';
import { AdoptionStep, PlanAdoptionFormTypes } from '../models/plan-adoption-form-models';
import QccoAdoptionDetailsForm from '../qcco-adoption/qcco-adoption-details-form';
import QccoIndemnifyForm from '../qcco-adoption/qcco-indemnify-form';
import QccoQuestionsForm from '../qcco-adoption/qcco-questions-form';
import QccoReview from '../qcco-adoption/qcco-review';
import { PlanAdoptionRoutes } from '../routes';


function QccoPlanAmendment() {
    const routeMatch = useRouteMatch();
    const formType = useQuery().get('formType')?.toLowerCase() as PlanAdoptionFormTypes;
    const dispatch = useDispatch();
    const history = useHistory();
    const location = useLocation();
    const { accessToken, decodedToken } = useContext(AuthContext) as any;

    const epiContent = useSelector<TcsmStates, TcsmEpiApiState>(state => state?.epiProps);
    const onSuccessUrl = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.InstitutionRoot}${ArcRoutes.PlanSummary}`);
    const institutionRootPath = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.InstitutionRoot}`);
    const featureRootPath = routeMatch?.path;

    const personLoaded = useDemographicsPerson(DemographicAppKeys.myCPG);
    const person = useSelector<TcsmStates, Person>(state => state?.personalInfo[DemographicAppKeys.myCPG].currentPerson);
    const currentInstitution = useSelector<TcsmStates, Institution>(state => state?.institutionState?.currentInstitution);

    const institutionState = useSelector<TcsmStates, InstitutionState>(state => state.institutionState);
    const summaryPlanLoaded = usePlanSummary(institutionState?.institutionClientNumber);
    const institutionId = institutionState?.currentInstitution?.org_client_nbr;

    const planSummary = institutionState?.planSummary;
    const dcActivePlanId = institutionState?.planSummary?.dc?.active[0]?.id;
    const rsvpActivePlanId = institutionState?.planSummary?.rsvp?.active[0]?.id;
    const currentStep: AdoptionStep = getAdoptionStepFromLocation(location);

    const [dcPlanDetail, setDcPlanDetail] = useState<PensionPlanDetail>(null);
    const [rsvpPlanDetail, setRsvpPlanDetail] = useState<PensionPlanDetail>(null);

    const wizard: any = GetQccoAmendmentWizard(
        { formType, dispatch, history, epiContent, onSuccessUrl, location, featureRootPath, institutionRootPath, person, currentInstitution, planSummary, authToken: accessToken, loginTime: decodedToken?.iat },
        dcPlanDetail,
        rsvpPlanDetail);

    const isIndemnificationRequired = wizard?.isIndemnificationRequired();

    useEffect(() => {
        if ((currentStep !== AdoptionStep.QUESTIONS) &&
            (currentStep !== AdoptionStep.INDEMNIFY)) return;

        switch (formType) {
            case PlanAdoptionFormTypes.LayDc:
                if (dcActivePlanId && !dcPlanDetail) dispatch(getDcPlanDetail(institutionId, dcActivePlanId, (res) => setDcPlanDetail(res?.data)));

                break;
            case PlanAdoptionFormTypes.Rsvp:
                if (rsvpActivePlanId && !rsvpPlanDetail) dispatch(getRsvpPlanDetail(institutionId, rsvpActivePlanId, (res) => setRsvpPlanDetail(res?.data)));

                break;
            default:
                break;
        }
    }, [dcActivePlanId, rsvpActivePlanId, currentStep, dcPlanDetail, rsvpPlanDetail]);

    if (!summaryPlanLoaded || !personLoaded || (!dcPlanDetail && !rsvpPlanDetail)) {
        return <Loader />;
    }

    if (!wizard) {
        return <Redirect to={institutionRootPath} />;
    }

    return <>

        <PlanAdoptionStepper wizard={wizard} institution={currentInstitution} />
        <Switch>
            <Route exact path={`${routeMatch.path}${PlanAdoptionRoutes.QccoQuestions}`}>
                <QccoQuestionsForm wizard={wizard} />
            </Route>

            <Route exact path={`${routeMatch.path}${PlanAdoptionRoutes.AdoptionDetails}`}>
                <QccoAdoptionDetailsForm wizard={wizard} />
            </Route>

            <Route exact path={`${routeMatch.path}${PlanAdoptionRoutes.Review}`}>
                <QccoReview wizard={wizard} />
            </Route>

            <Route exact path={`${routeMatch.path}${PlanAdoptionRoutes.Indemnify}`}>
                <QccoIndemnifyForm wizard={wizard} />
            </Route>

            <Route exact path={`${routeMatch.path}`}>
                {
                    isIndemnificationRequired &&
                    <Redirect to={`${routeMatch.path}${PlanAdoptionRoutes.Indemnify}${location.search}`} />
                }

                {
                    !isIndemnificationRequired &&
                    <Redirect to={`${routeMatch.path}${PlanAdoptionRoutes.QccoQuestions}${location.search}`} />
                }

            </Route>
        </Switch>
    </>;

}

export default QccoPlanAmendment;